// New-patient wizard for an unregistered chip: pet → owner → review & seal.
import { useEffect, useState } from "react";
import type { NewPetInput, OwnerSummary, RegisterResult } from "../../../shared/types";
import { api } from "../../api";
import { formatChip } from "./chip";

type Step = 0 | 1 | 2;
const today = () => new Date().toISOString().slice(0, 10);

export function RegisterPet({ chip, onDone, onCancel }: { chip: string; onDone: (r: RegisterResult, name: string) => void; onCancel: () => void }) {
  const [step, setStep] = useState<Step>(0);
  const [pet, setPet] = useState({ name: "", species: "Canine" as NewPetInput["species"], breed: "", sex: "" as NewPetInput["sex"] | "", color: "", birthDate: "" });
  const [ownerMode, setOwnerMode] = useState<"new" | "existing">("new");
  const [owner, setOwner] = useState({ fullName: "", phone: "", email: "", address: "" });
  const [existing, setExisting] = useState<OwnerSummary | null>(null);
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState<OwnerSummary[]>([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ownerMode !== "existing" || q.trim().length < 2) return setMatches([]);
    const t = setTimeout(() => api.owners(q).then(setMatches).catch(() => setMatches([])), 250);
    return () => clearTimeout(t);
  }, [q, ownerMode]);

  const petOk = pet.name.trim().length > 0;
  const ownerOk = ownerMode === "existing" ? !!existing : owner.fullName.trim().length > 0 && (!owner.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(owner.email));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.registerPet({
        microchip: chip,
        name: pet.name,
        species: pet.species,
        breed: pet.breed || undefined,
        sex: pet.sex || undefined,
        color: pet.color || undefined,
        birthDate: pet.birthDate || undefined,
        owner: ownerMode === "existing" ? { id: existing!.id } : { fullName: owner.fullName, phone: owner.phone || undefined, email: owner.email || undefined, address: owner.address || undefined },
      });
      onDone(r, pet.name);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  const P = (k: keyof typeof pet) => ({ value: pet[k] as string, onChange: (e: { target: { value: string } }) => setPet({ ...pet, [k]: e.target.value }) });
  const O = (k: keyof typeof owner) => ({ value: owner[k], onChange: (e: { target: { value: string } }) => setOwner({ ...owner, [k]: e.target.value }) });

  return (
    <div className="wizard">
      <div className="steps">
        {["Pet", "Owner", "Confirm"].map((s, i) => <div key={s} className={`st${i === step ? " on" : ""}${i < step ? " done" : ""}`}><b>{i < step ? "✓" : i + 1}</b>{s}</div>)}
      </div>
      <div className="chip-line">Microchip <code>{formatChip(chip)}</code></div>

      {step === 0 && (
        <div className="form">
          <div className="field"><label>Pet name *</label><input {...P("name")} maxLength={60} autoFocus placeholder="e.g. Fado" /></div>
          <div className="seg">
            {(["Canine", "Feline", "Other"] as const).map((s) => (
              <button key={s} className={pet.species === s ? "on" : ""} onClick={() => setPet({ ...pet, species: s })}>{s === "Canine" ? "🐕 Dog" : s === "Feline" ? "🐈 Cat" : "🐾 Other"}</button>
            ))}
          </div>
          <div className="field"><label>Breed</label><input {...P("breed")} maxLength={80} placeholder="e.g. Border Collie" /></div>
          <div className="frow">
            <div className="field"><label>Sex</label>
              <select {...P("sex")}><option value="">—</option><option>Male</option><option>Female</option><option>Neutered Male</option><option>Spayed Female</option></select>
            </div>
            <div className="field"><label>Date of birth</label><input type="date" max={today()} {...P("birthDate")} /></div>
          </div>
          <div className="field"><label>Colour</label><input {...P("color")} maxLength={60} placeholder="e.g. Black / white" /></div>
        </div>
      )}

      {step === 1 && (
        <div className="form">
          <div className="seg">
            <button className={ownerMode === "new" ? "on" : ""} onClick={() => setOwnerMode("new")}>New owner</button>
            <button className={ownerMode === "existing" ? "on" : ""} onClick={() => setOwnerMode("existing")}>Existing owner</button>
          </div>
          {ownerMode === "new" ? (
            <>
              <div className="field"><label>Full name *</label><input {...O("fullName")} maxLength={120} autoComplete="off" /></div>
              <div className="field"><label>Mobile</label><input {...O("phone")} type="tel" maxLength={40} /></div>
              <div className="field"><label>Email</label><input {...O("email")} type="email" maxLength={120} /></div>
              <div className="field"><label>Address</label><textarea {...O("address")} rows={2} maxLength={300} /></div>
            </>
          ) : (
            <>
              <div className="field"><label>Search by name, phone or email</label><input value={q} onChange={(e) => { setQ(e.target.value); setExisting(null); }} autoFocus /></div>
              <div className="owner-list">
                {matches.map((o) => (
                  <button key={o.id} className={`owner-opt${existing?.id === o.id ? " on" : ""}`} onClick={() => setExisting(o)}>
                    <div className="t1">{o.name}</div>
                    <div className="t3">{[o.phone, o.email, `${o.pets} pet${o.pets === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}</div>
                  </button>
                ))}
                {q.trim().length >= 2 && !matches.length && <div className="t3" style={{ padding: 8 }}>No owners found — switch to “New owner”.</div>}
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="form">
          <div className="review">
            <div className="rv-h">Pet</div>
            <div><b>{pet.name}</b> · {[pet.species, pet.breed, pet.sex, pet.color].filter(Boolean).join(" · ")}</div>
            {pet.birthDate && <div className="t3">Born {pet.birthDate}</div>}
            <div className="rv-h">Owner</div>
            <div>{ownerMode === "existing" ? <><b>{existing?.name}</b> (existing)</> : <><b>{owner.fullName}</b>{owner.phone && ` · ${owner.phone}`}{owner.email && ` · ${owner.email}`}</>}</div>
          </div>
          <label className="consent"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />The owner is present and agrees to register this pet on the Vetproof network.</label>
          <div className="fine">Registration seals the pet's identity as the genesis block of its record chain. It cannot be edited afterwards.</div>
        </div>
      )}

      {error && <div className="err">{error}</div>}
      <div className="wiz-f">
        <button className="btn ghost big" onClick={() => (step === 0 ? onCancel() : setStep((step - 1) as Step))}>{step === 0 ? "Cancel" : "Back"}</button>
        {step < 2
          ? <button className="btn teal big" disabled={step === 0 ? !petOk : !ownerOk} onClick={() => setStep((step + 1) as Step)}>Next</button>
          : <button className="btn teal big" disabled={!consent || busy} onClick={submit}>{busy ? "Sealing…" : "Register & seal"}</button>}
      </div>
    </div>
  );
}
